// Funções puras das visões Tabela, Calendário e Histórico da Industrialização.
// Tudo sai da planilha já carregada (DadosContext): datas reais de corte,
// bordo e furação (colunas BC/BD/BE) e o limite das máquinas (BG).

import { diasUteis, formatarData, parseNumero } from "../../utils/calculos";
import { hasValue } from "../../utils/data";
import { F } from "../../utils/gerencialFields";
import {
  SEQUENCIA_MAQUINAS,
  formatLoteLabel,
  getColunasMaquinas,
  getIndustrializacaoDeadline,
} from "../../utils/industrializacao";
import { compareDatesAsc, getDeadlineBucket, toDate } from "../../utils/status";

const MS_POR_DIA = 86_400_000;

function inicioDoDia(date) {
  const d = toDate(date);
  return d ? new Date(d.getFullYear(), d.getMonth(), d.getDate()) : null;
}

function chaveDia(date) {
  const d = inicioDoDia(date);
  if (!d) return "";
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;
}

// diasUteis() conta os dois extremos; "dias decorridos" nao conta o 1º dia.
function diasUteisEntre(inicio, fim) {
  const total = diasUteis(inicio, fim);
  return total == null ? null : Math.max(total - 1, 0);
}

/* ------------------------------------------------------------------ */
/* Tabela                                                               */
/* ------------------------------------------------------------------ */

/** Primeira operação (na sequência real) em que o lote ainda tem ambiente sem data. */
export function etapaAtualDoLote(lote) {
  const pendente = SEQUENCIA_MAQUINAS.find((operacao) =>
    lote.ambientes.some((ambiente) => ambiente.pendente && !hasValue(ambiente.datas[operacao.chave]))
  );
  return pendente ? `Aguardando ${pendente.titulo.toLowerCase()}` : "Máquinas concluídas";
}

/** Linhas da tabela no modo "Lote": uma por lote, com o progresso de cada operação. */
export function buildLinhasLote(lotes) {
  return lotes.map((lote) => ({
    id: lote.id,
    lote,
    loteLabel: lote.loteLabel,
    cliente: lote.clienteResumo,
    ambientes: `${lote.totalPendentes}/${lote.totalAmbientes}`,
    totalPendentes: lote.totalPendentes,
    totalAmbientes: lote.totalAmbientes,
    progresso: Object.fromEntries(
      SEQUENCIA_MAQUINAS.map((operacao) => [
        operacao.chave,
        {
          feitos: lote.totalAmbientes - lote.aguardandoPorOperacao[operacao.chave],
          total: lote.totalAmbientes,
        },
      ])
    ),
    etapa: etapaAtualDoLote(lote),
    limite: lote.dataLimiteMaquinas,
    status: lote.status,
    statusVariant: lote.statusVariant,
    valor: lote.valorTotal,
  }));
}

/** Linhas da tabela no modo "Ambiente": uma por ambiente PENDENTE, na ordem dos lotes. */
export function buildLinhasAmbiente(lotes) {
  return lotes.flatMap((lote) =>
    lote.ambientes
      .filter((ambiente) => ambiente.pendente)
      .map((ambiente, index) => ({
        id: `${lote.id}-${index}-${ambiente.ambiente}`,
        lote,
        loteLabel: lote.loteLabel,
        cliente: ambiente.cliente || lote.clientePrincipal,
        ambiente: ambiente.ambiente,
        mdfBordo: ambiente.mdfBordo,
        datas: ambiente.datas,
        etapa: ambiente.status,
        limite: ambiente.dataLimite,
        status: lote.status,
        statusVariant: lote.statusVariant,
        valor: ambiente.valor,
      }))
  );
}

const ORDENADORES = {
  loteLabel: (a, b) =>
    a.loteLabel.localeCompare(b.loteLabel, "pt-BR", { numeric: true, sensitivity: "base" }),
  cliente: (a, b) => String(a.cliente).localeCompare(String(b.cliente), "pt-BR", { sensitivity: "base" }),
  limite: (a, b) => compareDatesAsc(a.limite, b.limite),
  valor: (a, b) => (parseNumero(a.valor) || 0) - (parseNumero(b.valor) || 0),
};

export const COLUNAS_ORDENAVEIS = Object.keys(ORDENADORES);

/** Ordenação estável; sem ordenação escolhida, mantém a ordem dos lotes (limite mais cedo). */
export function ordenarLinhas(linhas, ordem) {
  const comparar = ordem && ORDENADORES[ordem.coluna];
  if (!comparar) return linhas;
  const sinal = ordem.direcao === "desc" ? -1 : 1;
  return linhas
    .map((linha, index) => ({ linha, index }))
    .sort((a, b) => sinal * comparar(a.linha, b.linha) || a.index - b.index)
    .map(({ linha }) => linha);
}

/* ------------------------------------------------------------------ */
/* Filtro dos realizados (calendário e histórico)                       */
/* ------------------------------------------------------------------ */

// Realizados olham a planilha inteira (lotes já concluídos também), então só os
// filtros de identidade valem aqui: busca, cliente e lote. Status e limite são
// da fila pendente e não fazem sentido para o que já saiu das máquinas.
export function linhaPassaFiltrosIdentidade(row, filters) {
  if (!hasValue(row[F.NUMERO_DO_LOTE])) return false;
  const loteLabel = formatLoteLabel(String(row[F.NUMERO_DO_LOTE]).trim());
  const cliente = String(row[F.NOME_DO_CLIENTE] ?? "");
  const busca = (filters?.busca || "").trim().toLowerCase();
  if (busca && !`${loteLabel} ${cliente} ${row[F.AMBIENTE] ?? ""}`.toLowerCase().includes(busca)) {
    return false;
  }
  if (filters?.cliente && filters.cliente !== "TODOS" && cliente.trim() !== filters.cliente) {
    return false;
  }
  const loteFiltro = (filters?.lote || "").trim().toLowerCase();
  if (loteFiltro && !loteLabel.toLowerCase().includes(loteFiltro)) return false;
  return true;
}

/** Toda operação de máquina com data real na planilha: {data, operacao, row, loteLabel, ...}. */
export function listarOperacoesRealizadas(dados, filters) {
  const eventos = [];
  dados.forEach((row) => {
    if (!linhaPassaFiltrosIdentidade(row, filters)) return;
    const loteId = String(row[F.NUMERO_DO_LOTE]).trim();
    SEQUENCIA_MAQUINAS.forEach((operacao) => {
      // "." na planilha = operação não se aplica: conta como feita, mas não tem data.
      const data = inicioDoDia(row[operacao.campo]);
      if (!data) return;
      eventos.push({
        data,
        operacao,
        loteId,
        loteLabel: formatLoteLabel(loteId),
        cliente: row[F.NOME_DO_CLIENTE] || "Cliente não informado",
        ambiente: row[F.AMBIENTE] || "Ambiente não informado",
        row,
      });
    });
  });
  return eventos;
}

/* ------------------------------------------------------------------ */
/* Calendário                                                           */
/* ------------------------------------------------------------------ */

export const VOCAB_CALENDARIO = {
  item: ["ambiente", "ambientes"],
  subitem: ["ambiente", "ambientes"],
  subitemTitulo: "Ambientes",
  grupoTitulo: "Lotes",
  previsao: ["previsão", "previsões"],
};

/** Ambientes pendentes no dia do limite das máquinas, agrupados pelo lote. */
export function buildEventosPendentes(lotes) {
  return lotes.flatMap((lote) =>
    lote.ambientes
      .filter((ambiente) => ambiente.pendente)
      .map((ambiente, index) => ({
        ambiente,
        index,
        data: inicioDoDia(ambiente.dataLimite || lote.dataLimiteMaquinas),
      }))
      .filter(({ data }) => data)
      .map(({ ambiente, index, data }) => ({
        id: `pendente-${lote.id}-${index}`,
        date: data,
        groupLabel: `${lote.loteLabel} · ${lote.clienteResumo}`,
        title: ambiente.ambiente,
        subtitle: `${ambiente.status} · limite das máquinas`,
        tone: lote.statusVariant,
        payload: { loteId: lote.id },
      }))
  );
}

/** Operações feitas no dia em que aconteceram (tom neutro, como "Carregado" em Entregas). */
export function buildEventosRealizados(dados, filters) {
  return listarOperacoesRealizadas(dados, filters).map((ev, index) => ({
    id: `realizado-${ev.loteId}-${ev.operacao.chave}-${index}`,
    date: ev.data,
    groupLabel: `${ev.loteLabel} · ${ev.cliente}`,
    title: `${ev.operacao.titulo} · ${ev.ambiente}`,
    subtitle: `${ev.operacao.titulo} realizado`,
    tone: "neutral",
    payload: { loteId: ev.loteId },
  }));
}

/* ------------------------------------------------------------------ */
/* Histórico                                                            */
/* ------------------------------------------------------------------ */

export const PERIODOS_HISTORICO = [
  { id: "30", label: "30 dias", dias: 30 },
  { id: "60", label: "60 dias", dias: 60 },
  { id: "90", label: "90 dias", dias: 90 },
  { id: "ano", label: "Este ano" },
];

export function intervaloDoPeriodo(periodoId, hoje = new Date()) {
  const fim = inicioDoDia(hoje);
  if (periodoId === "ano") return { inicio: new Date(fim.getFullYear(), 0, 1), fim };
  const periodo = PERIODOS_HISTORICO.find((p) => p.id === periodoId) || PERIODOS_HISTORICO[0];
  const inicio = new Date(fim);
  inicio.setDate(inicio.getDate() - (periodo.dias - 1));
  return { inicio, fim };
}

function dentro(data, { inicio, fim }) {
  return data && data >= inicio && data <= fim;
}

function segundaDaSemana(date) {
  const d = inicioDoDia(date);
  const deslocamento = (d.getDay() + 6) % 7; // segunda = 0
  d.setDate(d.getDate() - deslocamento);
  return d;
}

/** Volume por semana (segunda a domingo) de cada operação, cobrindo o período inteiro. */
export function producaoPorSemana(realizados, intervalo) {
  const semanas = [];
  for (
    let cursor = segundaDaSemana(intervalo.inicio);
    cursor <= intervalo.fim;
    cursor = new Date(cursor.getTime() + 7 * MS_POR_DIA)
  ) {
    semanas.push({
      chave: chaveDia(cursor),
      inicio: new Date(cursor),
      label: formatarData(cursor).slice(0, 5),
      ...Object.fromEntries(SEQUENCIA_MAQUINAS.map((op) => [op.chave, 0])),
    });
  }
  const porChave = new Map(semanas.map((semana) => [semana.chave, semana]));
  realizados.forEach((ev) => {
    if (!dentro(ev.data, intervalo)) return;
    const semana = porChave.get(chaveDia(segundaDaSemana(ev.data)));
    if (semana) semana[ev.operacao.chave] += 1;
  });
  return semanas;
}

/** Dias com alguma operação, do mais recente ao mais antigo, com os lotes de cada operação. */
export function producaoPorDia(realizados, intervalo) {
  const dias = new Map();
  realizados.forEach((ev) => {
    if (!dentro(ev.data, intervalo)) return;
    const chave = chaveDia(ev.data);
    if (!dias.has(chave)) {
      dias.set(chave, {
        chave,
        data: ev.data,
        total: 0,
        ...Object.fromEntries(SEQUENCIA_MAQUINAS.map((op) => [op.chave, []])),
      });
    }
    const dia = dias.get(chave);
    dia[ev.operacao.chave].push(ev);
    dia.total += 1;
  });
  return [...dias.values()].sort((a, b) => b.data - a.data);
}

/**
 * Ambiente que terminou as máquinas: todas as operações preenchidas (data ou
 * "." de não se aplica) e ao menos uma data real. Conclusão = a data mais
 * tardia entre elas.
 */
export function conclusaoDasMaquinas(row) {
  if (!SEQUENCIA_MAQUINAS.every((operacao) => hasValue(row[operacao.campo]))) return null;
  const datas = SEQUENCIA_MAQUINAS.map((operacao) => inicioDoDia(row[operacao.campo])).filter(Boolean);
  if (!datas.length) return null;
  return new Date(Math.max(...datas.map((d) => d.getTime())));
}

/** Ambientes que concluíram as máquinas no período, com prazo e tempo de máquinas. */
export function ambientesConcluidos(dados, filters, intervalo) {
  return dados
    .filter((row) => linhaPassaFiltrosIdentidade(row, filters))
    .map((row) => {
      const conclusao = conclusaoDasMaquinas(row);
      if (!dentro(conclusao, intervalo)) return null;
      const limite = inicioDoDia(getIndustrializacaoDeadline(row));
      const mdf = inicioDoDia(row[F.MDF_BORDO]);
      const loteId = String(row[F.NUMERO_DO_LOTE]).trim();
      const atrasoDias = limite && conclusao > limite ? diasUteisEntre(limite, conclusao) : 0;
      return {
        loteId,
        loteLabel: formatLoteLabel(loteId),
        cliente: row[F.NOME_DO_CLIENTE] || "Cliente não informado",
        ambiente: row[F.AMBIENTE] || "Ambiente não informado",
        mdf,
        conclusao,
        limite,
        noPrazo: limite ? conclusao <= limite : null,
        atrasoDias,
        tempoMaquinas: mdf && conclusao >= mdf ? diasUteisEntre(mdf, conclusao) : null,
      };
    })
    .filter(Boolean)
    .sort((a, b) => b.conclusao - a.conclusao);
}

function media(valores) {
  const validos = valores.filter((v) => v != null);
  if (!validos.length) return null;
  return Math.round((validos.reduce((soma, v) => soma + v, 0) / validos.length) * 10) / 10;
}

/** Tempo de máquinas por lote: do primeiro MDF à última conclusão do lote no período. */
export function temposPorLote(concluidos) {
  const lotes = new Map();
  concluidos.forEach((amb) => {
    if (!lotes.has(amb.loteId)) {
      lotes.set(amb.loteId, {
        loteId: amb.loteId,
        loteLabel: amb.loteLabel,
        clientes: new Set(),
        ambientes: 0,
        noPrazo: 0,
        comLimite: 0,
        mdf: null,
        conclusao: null,
      });
    }
    const lote = lotes.get(amb.loteId);
    lote.clientes.add(amb.cliente);
    lote.ambientes += 1;
    if (amb.noPrazo != null) {
      lote.comLimite += 1;
      if (amb.noPrazo) lote.noPrazo += 1;
    }
    if (amb.mdf && (!lote.mdf || amb.mdf < lote.mdf)) lote.mdf = amb.mdf;
    if (!lote.conclusao || amb.conclusao > lote.conclusao) lote.conclusao = amb.conclusao;
  });
  return [...lotes.values()]
    .map((lote) => ({
      ...lote,
      cliente: [...lote.clientes].join(", "),
      tempoMaquinas: lote.mdf ? diasUteisEntre(lote.mdf, lote.conclusao) : null,
    }))
    .sort((a, b) => b.conclusao - a.conclusao);
}

export function buildHistorico(dados, filters, periodoId, hoje = new Date()) {
  const intervalo = intervaloDoPeriodo(periodoId, hoje);
  const realizados = listarOperacoesRealizadas(dados, filters);
  const concluidos = ambientesConcluidos(dados, filters, intervalo);
  const comLimite = concluidos.filter((amb) => amb.noPrazo != null);
  const noPrazo = comLimite.filter((amb) => amb.noPrazo).length;

  return {
    intervalo,
    semanas: producaoPorSemana(realizados, intervalo),
    dias: producaoPorDia(realizados, intervalo),
    concluidos,
    atrasados: concluidos.filter((amb) => amb.noPrazo === false),
    lotes: temposPorLote(concluidos),
    kpis: {
      operacoes: realizados.filter((ev) => dentro(ev.data, intervalo)).length,
      concluidos: concluidos.length,
      percentualNoPrazo: comLimite.length ? Math.round((noPrazo / comLimite.length) * 100) : null,
      noPrazo,
      comLimite: comLimite.length,
      tempoMedio: media(concluidos.map((amb) => amb.tempoMaquinas)),
    },
  };
}

export const OPERACOES_EXIBICAO = getColunasMaquinas();

/* ------------------------------------------------------------------ */
/* Kanban                                                               */
/* ------------------------------------------------------------------ */

/**
 * Um marcador por operação, na ordem das colunas: feito (com data) ou pendente.
 * `proxima` marca a primeira pendente — o que a fábrica tem de fazer agora.
 */
export function marcadoresDoAmbiente(ambiente) {
  let proximaMarcada = false;
  return OPERACOES_EXIBICAO.map((operacao) => {
    const valor = ambiente.datas[operacao.chave];
    const naoSeAplica = hasValue(valor) && String(valor).trim() === ".";
    const feito = hasValue(valor);
    const proxima = !feito && !proximaMarcada;
    if (proxima) proximaMarcada = true;
    return {
      chave: operacao.chave,
      sigla: operacao.titulo.charAt(0),
      titulo: operacao.titulo,
      feito,
      proxima,
      data: naoSeAplica ? null : inicioDoDia(valor),
      naoSeAplica,
    };
  });
}

/**
 * Selo do prazo no card: o estado em palavra + a data. Usa o mesmo
 * getDeadlineBucket que define a cor do lote, para texto e cor nunca divergirem.
 */
export function rotuloPrazoLote(limite, hoje = new Date()) {
  const data = inicioDoDia(limite);
  if (!data) return "Sem limite";
  const dia = formatarData(data).slice(0, 5);
  switch (getDeadlineBucket(data, hoje)) {
    case "overdue":
      return `Atrasado ${diasUteisEntre(data, inicioDoDia(hoje))} du · ${dia}`;
    case "today":
      return "Vence hoje";
    case "this_week":
      return `Esta semana · ${dia}`;
    default:
      return `No prazo · ${dia}`;
  }
}
